/**
 * Role List Page
 * Page for listing and managing roles
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Role } from '../../types/rbac.types';
import { getRoles, deleteRole } from '../../services/roleService';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { RouteActionButton } from '../../components/common';

/** Extrai a mensagem de erro da resposta da API, preservando o fallback. */
const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const apiError = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof apiError === 'string' && apiError ? apiError : fallback;
};

const columns: DataTableColumn<Role>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (role) => (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption">Técnico: {role.name}</Typography>
            <br />
            <Typography variant="caption">Nível: {role.level}</Typography>
            {role.department && (
              <>
                <br />
                <Typography variant="caption">Depto: {role.department}</Typography>
              </>
            )}
          </Box>
        }
        arrow
        placement="right"
      >
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {role.displayName}
          </Typography>
          {role.description && (
            <Typography variant="caption" color="text.secondary">
              {role.description}
            </Typography>
          )}
        </Box>
      </Tooltip>
    ),
  },
  {
    id: 'permissions',
    label: 'Permissões',
    align: 'center',
    render: (role) => (
      <Chip label={role.permissionCount ?? 0} size="small" color="primary" variant="outlined" />
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (role) => (
      <Box display="flex" gap={0.5} justifyContent="center">
        <Chip
          label={role.isActive ? 'Ativo' : 'Inativo'}
          size="small"
          color={role.isActive ? 'success' : 'default'}
        />
        {role.isSystemRole && <Chip label="Sistema" size="small" color="warning" />}
      </Box>
    ),
  },
];

const RoleListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const permissoesDaFuncao = useControle('/roles/:id/permissions', pode('role.update'), 'Permissões');
  const editar = useControle('/roles/:id/edit', pode('role.update'), 'Editar');
  const excluir = useAcao('role.delete', 'Excluir');

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getRoles(
        { search: searchTerm ?? undefined, roleType: 'global' },
        { page: page + 1, limit: rowsPerPage, sortBy: 'displayName', sortOrder: 'ASC' }
      );

      setRoles(response.data);
      setTotalRows(response.meta.total);
    } catch (err: unknown) {
      console.error('Error loading roles:', err);
      setError(getApiErrorMessage(err, 'Erro ao carregar funções'));
      enqueueSnackbar('Erro ao carregar funções', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, enqueueSnackbar]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleEdit = (role: Role) => {
    navigate(`/roles/${role.id}/edit`);
  };

  const handleManagePermissions = (role: Role) => {
    navigate(`/roles/${role.id}/permissions`);
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      setDeleting(true);
      await deleteRole(roleToDelete.id);
      enqueueSnackbar('Função excluída com sucesso!', { variant: 'success' });
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      loadRoles();
    } catch (err: unknown) {
      console.error('Error deleting role:', err);
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao excluir função'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Funções' }]}
      title="Funções"
      actions={
        <RouteActionButton
          para="/roles/create"
          exige={pode('role.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Nova Função
        </RouteActionButton>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Buscar funções..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 300, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={roles}
        getRowId={(role) => role.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma função encontrada',
          description:
            'As funções agrupam permissões: além das que acompanham a plataforma, novas funções são criadas nesta tela.',
        }}
        actions={[
          {
            icon: <SecurityIcon fontSize="small" />,
            onClick: handleManagePermissions,
            ...permissoesDaFuncao,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            hidden: (role) => role.isSystemRole,
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: totalRows,
          onPageChange: setPage,
          onRowsPerPageChange: (newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setPage(0);
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a função <strong>{roleToDelete?.displayName}</strong>?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default RoleListPage;
